// @flow

import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withRouter } from 'shared/routing/withRouter';
import { storeRetailPrice } from '../../pay/action';
import Spinner from '../../../components/contentLoader/spinner';
import ErrorDialogBox from '../../../components/Common/dialogContainer';
import {
    getCartItems,
    showOrHideDetailOfItem,
    removeItemFromCart,
    increaseQuantity,
    decreaseQuantity,
    updateAddToCart,
} from '../action';
import queryString from 'query-string';
import { firstQueryValue } from 'shared/util/queryParams';
import increaseQuantityIcon from '../../../assets/increment.png';
import decreaseQuantityIcon from '../../../assets/decrement.png';
import placeHolder from '../../../assets/gallery_place_holder.png';
import storeGroupIcon from '../../../assets/storeIcon.png';
import onlOffGroupIcon from '../../../assets/web_hi_res_512.png';
import goldIcon from '../../../assets/ic_goldstart.png';
import platIcon from '../../../assets/ic_partner.png';
import { storeHashKey } from '../../detail/action';

type CartProps = {
    location: { search: string };
    history: { replace: (arg: { pathname: string; search: string }) => void };
    cartItems: any[];
    cartAmount: number;
    totalShipping: number;
    isLoading: boolean;
    paymentHashKey: string;
    getCartItems: (url: string, uid: string) => Promise<unknown> | unknown;
    removeItemFromCart: (obj: unknown, index: number) => Promise<unknown> | unknown;
    updateAddToCart: (obj: unknown) => Promise<any> | unknown;
    storeRetailPrice: (amount: number) => void;
    storeHashKey: (hash: string) => void;
    increaseQuantity: (item: unknown, index: number) => void;
    decreaseQuantity: (item: unknown, index: number) => void;
    showOrHideDetailOfItem: (adKey: unknown, index: number) => void;
};

function Cart(props: CartProps) {
    const { location, getCartItems } = props;

    const parsed = useMemo(() => queryString.parse(location.search), [location.search]);
    const uid = useMemo(() => firstQueryValue(parsed.uid as any), [parsed]);
    const url = useMemo(() => firstQueryValue(parsed.url as any), [parsed]);

    const [errMsg, setErrMsg] = useState('');
    const [isErrorDialogVisible, setIsErrorDialogVisible] = useState(false);
    const [localLoading, setLocalLoading] = useState(true);

    const loadCart = useCallback(() => {
        if (!url || !uid) {
            setErrMsg('Missing cart url or user id');
            setIsErrorDialogVisible(true);
            setLocalLoading(false);
            return;
        }
        setLocalLoading(true);
        const p = getCartItems(url, uid);
        const finish = () => setLocalLoading(false);
        if (p != null && typeof (p as Promise<unknown>).then === 'function') {
            (p as Promise<unknown>).then(finish).catch(finish);
        } else {
            finish();
        }
    }, [getCartItems, url, uid]);

    useEffect(() => {
        loadCart();
    }, [loadCart]);

    const onBuyClick = useCallback(() => {
        const cartAmount = props.cartAmount;
        setLocalLoading(true);
        const setModel = {
            UserId: uid,
            ValidatorKey: props.paymentHashKey,
            cartList: props.cartItems,
        };
        props.storeRetailPrice(cartAmount);
        const search = location.search;
        if (cartAmount <= 0) {
            setErrMsg('Please add some cart');
            setIsErrorDialogVisible(true);
        }
        const p = props.updateAddToCart(setModel);
        const finish = () => setLocalLoading(false);
        if (p != null && typeof (p as Promise<unknown>).then === 'function') {
            (p as Promise<any>)
                .then((resp) => {
                    if (resp.value.data.Status == 1) {
                        props.storeHashKey(resp.value.data.PaymentHash);
                        setTimeout(() => {
                            window.location.href =
                                '/pay' +
                                search +
                                '&paymentHash=' +
                                props.paymentHashKey +
                                '&PaymentHashToRedirctPaymentGate=' +
                                resp.value.data.PaymentHash +
                                '&totalAmount=' +
                                cartAmount +
                                '&shippingCharge=' +
                                props.totalShipping;
                        }, 1000);
                    }
                })
                .catch(() => {
                    finish();
                });
        } else {
            finish();
        }
    }, [props, uid, location.search]);

    const closeDialog = useCallback(() => {
        setErrMsg('');
        setIsErrorDialogVisible(false);
    }, []);

    const increaseQty = useCallback(
        (item: unknown, index: number) => {
            props.increaseQuantity(item, index);
        },
        [props]
    );
    const decreaseQty = useCallback(
        (item: unknown, index: number) => {
            props.decreaseQuantity(item, index);
        },
        [props]
    );

    const onRemoveItem = useCallback(
        (item: any, index: number) => {
            setLocalLoading(true);
            const obj = {
                Key: item.CartKey,
                ProductKey: item.AdKey,
                UserKey: uid,
            };
            const p = props.removeItemFromCart(obj, index);
            const finish = () => setLocalLoading(false);
            if (p != null && typeof (p as Promise<unknown>).then === 'function') {
                (p as Promise<unknown>).then(finish).catch(finish);
            } else {
                finish();
            }
        },
        [props, uid]
    );

    const closeWindow = useCallback(() => {
        const w = window as any;
        // React Native WebView: notify native app to close/go back.
        if (w.ReactNativeWebView && typeof w.ReactNativeWebView.postMessage === 'function') {
            try {
                w.ReactNativeWebView.postMessage(
                    JSON.stringify({ type: 'CLOSE_WEBVIEW', source: 'cart_empty_ok' })
                );
            } catch (_error) {
                w.ReactNativeWebView.postMessage('CLOSE_WEBVIEW');
            }
            // Send legacy/plain payloads for apps expecting simple strings.
            w.ReactNativeWebView.postMessage('CLOSE_WEBVIEW');
            w.ReactNativeWebView.postMessage('close');
            w.ReactNativeWebView.postMessage('goBack');
            setTimeout(() => {
                if (w.history.length > 1) {
                    w.history.back();
                }
            }, 120);
            return;
        }
        // iOS WKWebView style bridge fallback (if available).
        if (w.webkit && w.webkit.messageHandlers && w.webkit.messageHandlers.closeWindow) {
            try {
                w.webkit.messageHandlers.closeWindow.postMessage('close');
                return;
            } catch (_error) {
                // continue with browser fallback
            }
        }
        // Browser fallback.
        if (w.history.length > 1) {
            w.history.back();
            return;
        }
        w.close();
    }, []);

    const totalShipping = props.totalShipping;
    const amount = props.cartAmount;
    const totalAmount = props.cartAmount + props.totalShipping;

    const payList =
        props.cartItems.length > 0 &&
        props.cartItems.map((listItem: any, index: number) => {
            let Icon: string | null = null;
            if (listItem.SegmentCode.trim() == 'PLAT') {
                Icon = platIcon;
            } else if (listItem.SegmentCode.trim() == 'GOLD') {
                Icon = goldIcon;
            }
            return (
                <div key={index} className="row">
                    <div className="col-sm-12 col-md-12 col-lg-12 no-Pd">
                        <div className="card card-pd card-mr">
                            <div className="row">
                                <div className="col-xs-2 col-sm-3 col-md-3 col-lg-3 img-Box">
                                    {listItem.AdImagePath !== null && listItem.AdImagePath !== '' ? (
                                        <img src={listItem.AdImagePath} />
                                    ) : (
                                        <img src={placeHolder} />
                                    )}
                                </div>

                                <div className="col-xs-10 col-sm-9 col-md-9 col-lg-9" style={{ padding: '3px 0px' }}>
                                    <section style={{ lineHeight: '16px' }}>
                                        <div className="card-text Fw primaryTextColor">{listItem.AdName}</div>

                                        <span className="card-text F5 Mr-R">
                                            {Icon !== null ? <img src={Icon} className="segmentIcon" /> : null}
                                            {listItem.SupplierName}
                                        </span>

                                        <span className="card-text F5">
                                            {listItem.GroupCode == 'ONL' ? (
                                                <img src={storeGroupIcon} style={{ height: 11 }} className="segmentIcon" />
                                            ) : (
                                                <img src={onlOffGroupIcon} style={{ height: 11 }} className="segmentIcon" />
                                            )}
                                            {listItem.GroupName}
                                        </span>
                                    </section>

                                    <section style={{ padding: '5px 0px 3px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div className="card-text F3" style={{ paddingRight: '15px' }}>
                                                <div className="F3 flex-row">
                                                    <span className="F3 amtThrough">₹ {parseInt(listItem.MRP)}</span>
                                                    <div className="F3 Fw primaryColor discountBoxCrt">
                                                        {listItem.CategoryCode == 'ECOM1' ||
                                                        listItem.CategoryCode == 'ECOM2' ||
                                                        listItem.CategoryCode == 'ECOM3'
                                                            ? listItem.Discount + '% OFF'
                                                            : 'SAVE ' + listItem.Discount + '%'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="card-text F2">
                                                <div className="F2 amtMain"> ₹ {parseInt(listItem.RetailPrice)}</div>
                                            </div>
                                        </div>
                                    </section>

                                    <section>
                                        <div className="row">
                                            <div className="col-sm-12 col-md-12 no-Pd">
                                                <div className="flex-container-grid" style={{ lineHeight: '8px' }}>
                                                    <div className="card-text F4" style={{ display: 'contents' }}>
                                                        {listItem.CategoryCode == 'ECOM1' ||
                                                        listItem.CategoryCode == 'ECOM2' ||
                                                        listItem.CategoryCode == 'ECOM3' ? (
                                                            <div className="align-quantity-selection" style={{}}>
                                                                <div style={{ marginRight: 8 }}>Quantity</div>

                                                                {listItem.SelectedQuantity > 1 ? (
                                                                    <img
                                                                        src={decreaseQuantityIcon}
                                                                        style={{ width: 20, height: 20 }}
                                                                        onClick={() => decreaseQty(listItem, index)}
                                                                    />
                                                                ) : (
                                                                    <img
                                                                        src={decreaseQuantityIcon}
                                                                        style={{ width: 20, height: 20 }}
                                                                        className="greyImage-quantity"
                                                                    />
                                                                )}
                                                                <span
                                                                    style={{
                                                                        color: '#13679c',
                                                                        fontWeight: 'bold',
                                                                        margin: '8px 10px',
                                                                        fontSize: 15,
                                                                    }}
                                                                >
                                                                    {listItem.SelectedQuantity}
                                                                </span>
                                                                {listItem.SelectedQuantity < listItem.QuantityLimit ? (
                                                                    <img
                                                                        src={increaseQuantityIcon}
                                                                        style={{ width: 20, height: 20 }}
                                                                        onClick={() => increaseQty(listItem, index)}
                                                                    />
                                                                ) : (
                                                                    <img
                                                                        src={increaseQuantityIcon}
                                                                        style={{ width: 20, height: 20 }}
                                                                        className="greyImage-quantity"
                                                                    />
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="align-quantity-selection" style={{}}>
                                                                <div style={{ marginRight: 8 }}>Quantity</div>
                                                                <img
                                                                    src={decreaseQuantityIcon}
                                                                    style={{ width: 20, height: 20 }}
                                                                    className="greyImage-quantity"
                                                                />
                                                                <span
                                                                    style={{
                                                                        color: '#13679c',
                                                                        fontWeight: 'bold',
                                                                        margin: '8px 10px',
                                                                        fontSize: 15,
                                                                    }}
                                                                >
                                                                    {listItem.SelectedQuantity}
                                                                </span>
                                                                <img
                                                                    src={increaseQuantityIcon}
                                                                    style={{ width: 20, height: 20 }}
                                                                    className="greyImage-quantity"
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="card-text F3">
                                                            <i
                                                                className="fa fa-trash TrashIcon"
                                                                onClick={() => onRemoveItem(listItem, index)}
                                                                aria-hidden="true"
                                                            ></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </div>

                            <div className="collapseCard">
                                <div className="col-md-12 col-sm-12 card-text F4 no-Pd">
                                    <div className="vldDt"> Reduce maintenance ₹ {listItem.DisplayReward}</div>
                                    {listItem.AdDescription}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });

    const isBusy = localLoading || props.isLoading;

    return (
        <div className="Card-container" style={{ overflowY: 'hidden' }}>

      <div className="row">
          <div className="col-sm-12 col-md-12 col-lg-12 no-Pd">
            <div className="card">
             
                  <div className="card-body rewardCard">
           
                  <div className="flex-row">
                    <div className="card-text F3"></div>
                    <div className="card-text F3"></div>
                    <div className="card-text F3">Amount</div>
                  </div>

                  <div className="flex-row" style={{padding:'4px 0px 10px'}}>
                    <div className="card-text F1 Fw Lh-2">Cart</div>
                    <div className="card-text F1 Fw Lh-2"></div>
                    <div className="card-text F1 Fw Lh-2">{amount !== undefined ? '₹ ' + amount  : '₹ '+'0'}</div>
                  </div>

              </div>
            </div>
          </div>
      </div>
      {isBusy ? <Spinner /> : null}

      {props.cartItems.length > 0 && !isBusy ?

      <div>

        <section className="rewardCardSection" style={totalShipping !== 0 ? { paddingBottom:"8em"} : { paddingBottom:"40px"}}>
           {payList}
        </section>

          <ErrorDialogBox errorMsg={errMsg} isErrorDialogVisible={isErrorDialogVisible} closeDialog={closeDialog} />
        <footer>
       
          {totalShipping !== 0 ?
        <div className="card shadow-none totalsection-fixed" style={{boxShadow:'none'}}>
        <div className="flex-row" style={{padding:"0px 10px"}}>
          <div className="F3 amtMain secondoryTextColor">Amount</div>
          <div className="F3 amtMain secondoryTextColor"> {amount !== undefined ? '₹ ' + parseInt(String(amount), 10)  : '₹ '+'0'}</div>
        </div>
          <div className="flex-row" style={{padding:"3px 10px"}}>
          <div className="F5 amtMain secondoryTextColor">Shipping</div>
          <div className="F5 amtMain secondoryTextColor"> {totalShipping !== undefined ? '₹ ' + parseInt(String(totalShipping), 10)  : '₹ '+'0'}</div>
        </div>
          <div className="flex-row" style={{padding:"5px 10px"}}>
          <div className="F3 amtMain">Total Amount</div>
          <div className="F3 amtMain"> {totalAmount !== undefined ? '₹ ' + parseInt(String(totalAmount), 10)  : '₹ '+'0'}</div>
        </div>
                  <div className="card termsCard-fixed">
            <div style={{color:"black"}}>By Clicking below, I agree to the <a href="https://www.societynotebook.com/terms/P2TermsAndCond.html" target="_blank" rel="noreferrer">Terms & Condition</a> and <a href="https://www.societynotebook.com/terms/Policy.html" target="_blank" rel="noreferrer">Refund Policy</a></div>
          </div>
        </div>
        
        : 
           <div className="card termsCard-fixed">
            <div style={{color:"black"}}>By Clicking below, I agree to the <a href="https://www.societynotebook.com/terms/P2TermsAndCond.html" target="_blank" rel="noreferrer">Terms & Condition</a> and <a href="https://www.societynotebook.com/terms/Policy.html" target="_blank" rel="noreferrer">Refund Policy</a></div>
          </div>
        }
        

          <div className="col-sm-12 col-md-12 col-lg-12 no-Pd">
                <button
                  type="submit"
                  className="BuyBtnFooter primaryColor-ph2"
                  onClick={onBuyClick}
                  >
                  PROCEED TO PAY
                </button>
          </div>
        </footer>
      </div>: 
      !isBusy && props.cartItems.length == 0 ?
        <div >
          <div style={{color:"black"}} className="no-item-cart">NO ITEM TO DISPLAY</div>
              <button
                type="button"
                onClick={closeWindow}
                className="BuyBtnFooter primaryColor-ph2"
              >
               <div className="align-text-ok">OK</div>
              </button>
        </div>:null
      }
      </div>
    );
}

const mapStateToProps = state => {
  return {
    cartItems:state.cart.cartItems,
    cartAmount: state.cart.cartAmount,
    totalShipping: state.cart.totalShipping,
    adDetail:state.detail.adDetail,
    isLoading:state.cart.isLoading,
    selectedQuantity:state.detail.selectedQuantity,
    paymentHashKey:state.cart.paymentHashKey

  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      getCartItems,
      showOrHideDetailOfItem,
      removeItemFromCart,
      storeRetailPrice,
      increaseQuantity,
      decreaseQuantity,
      updateAddToCart,
      storeHashKey
    },
    dispatch
  );
};

// #endregion
export default compose(
  connect(mapStateToProps, mapDispatchToProps),withRouter
)(Cart);
